// Ambient background owned by AppShell. Absolute positioning is load-bearing:
// fixed positioning changes the containing block and breaks the scaled geometry.
import type { CSSProperties } from 'react';
import { useScrimStore } from './dialog/scrim-store';

interface BlobSpec {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  size: string;
  color: string; // var(--ds-aurora-blob-N) — exact per-theme hsl from the reference
  blur: string; // var(--ds-aurora-blob-N-blur) — exact per-theme px from the reference
  animation: string;
  duration: string; // var(--ds-aurora-blob-N-dur) — exact per-theme duration from the reference
  delay: string;
}

// Left-edge trio (feeds the sidebar) + one ambient blob upper-right. Position
// proportions derived from the reference's ~1340×900 demo canvas; color/blur/
// duration reference the exact per-blob, per-theme tokens (tokens.css).
const BLOBS: BlobSpec[] = [
  { top: '-16.7vh', left: '-9.7vw', size: '47.8vmin', color: 'var(--ds-aurora-blob-1)', blur: 'var(--ds-aurora-blob-1-blur)', animation: 'ds-float-y', duration: 'var(--ds-aurora-blob-1-dur)', delay: '0s' },
  { top: '38%', left: '-3vw', size: '40vmin', color: 'var(--ds-aurora-blob-2)', blur: 'var(--ds-aurora-blob-2-blur)', animation: 'ds-float-y2', duration: 'var(--ds-aurora-blob-2-dur)', delay: '-4s' },
  { bottom: '-16.7vh', left: '-5.2vw', size: '35.6vmin', color: 'var(--ds-aurora-blob-3)', blur: 'var(--ds-aurora-blob-3-blur)', animation: 'ds-float-y', duration: 'var(--ds-aurora-blob-3-dur)', delay: '-8s' },
  { top: 'var(--ds-aurora-blob-4-top)', right: '8vw', size: '44.4vmin', color: 'var(--ds-aurora-blob-4)', blur: 'var(--ds-aurora-blob-4-blur)', animation: 'ds-float-y2', duration: 'var(--ds-aurora-blob-4-dur)', delay: '-3s' },
];

function blobStyle(b: BlobSpec, paused: boolean): CSSProperties {
  return {
    position: 'absolute',
    top: b.top,
    bottom: b.bottom,
    left: b.left,
    right: b.right,
    width: b.size,
    height: b.size,
    borderRadius: '50%',
    background: `radial-gradient(circle, ${b.color}, transparent 70%)`,
    filter: `blur(${b.blur})`,
    animation: `${b.animation} ${b.duration} ease-in-out infinite`,
    animationDelay: b.delay,
    animationPlayState: paused ? 'paused' : 'running',
    willChange: 'transform',
  };
}

export function Aurora() {
  // Ambient drift is paused while any Dialog/Confirm scrim is open — a static
  // aurora gives the sidebar/scrim/dialog glass sitting above it nothing to
  // recompute every frame, since backdrop-filter only pays its cost when the
  // content underneath actually changes. See DESIGN_SYSTEM.md §5.5.
  const paused = useScrimStore((s) => s.openCount > 0);

  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        zIndex: 0,
        pointerEvents: 'none',
        background: 'var(--ds-canvas-wash)',
      }}
    >
      {BLOBS.map((b, i) => (
        <div key={i} style={blobStyle(b, paused)} />
      ))}
    </div>
  );
}
